import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Box,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import {
  PersonAdd as AddCustomerIcon,
  Inventory2 as AddItemIcon,
  Search as SearchCustomerIcon,
  ViewList as BrowseItemsIcon,
  QrCodeScanner as ScanBarcodeIcon,
  Settings as SettingsIcon,
  PersonAdd,
  AccountBalance as AddDebtIcon,
  Payment as AddPaymentIcon,
  PointOfSale as NewSaleIcon,
  DragIndicator as DragIcon,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Role } from '../../types';
import { PageAccentName } from '../../theme/pageAccents';
import { BarcodeScannerDialog } from '../barcode';
import PageIconTile from '../common/PageIconTile';
import { createInteractiveCardSx } from '../../theme/interactions';

// Define available quick actions
export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  accent: PageAccentName;
  requiredRoles?: Role[];
  category: 'customer' | 'item' | 'user' | 'search' | 'debt' | 'other';
}

interface QuickActionsProps {
  onAddCustomer?: () => void;
  onAddItem?: () => void;
  onSearchCustomer?: () => void;
  onAddDebt?: () => void;
  onAddPayment?: () => void;
}

const DEFAULT_ENABLED_ACTIONS = [
  'new-sale',
  'add-customer',
  'add-item',
  'add-debt',
  'add-payment',
  'search-customer',
  'browse-items',
  'scan-barcode',
];
const QUICK_ACTION_STORAGE_PREFIX = 'veresiye_quick_actions_';
const QUICK_ACTION_STORAGE_VERSION = '2';
const QUICK_ACTION_STORAGE_VERSION_SUFFIX = '_version';
const NEWLY_INTRODUCED_ACTIONS = ['new-sale'];
const QUICK_ACTION_IDS = new Set([...DEFAULT_ENABLED_ACTIONS, 'add-user']);

interface DragRowMetric {
  top: number;
  height: number;
  marginBottom: number;
}

const arrangeVisibleActions = (
  allIds: string[],
  visibleIds: string[],
  nextVisibleIds: string[]
): string[] => {
  const visibleSet = new Set(visibleIds);
  let nextIndex = 0;
  return allIds.map(id => visibleSet.has(id) ? nextVisibleIds[nextIndex++] : id);
};

const readEnabledActions = (userId?: string): string[] => {
  if (!userId) return [...DEFAULT_ENABLED_ACTIONS];

  try {
    const savedActionsJson = localStorage.getItem(`${QUICK_ACTION_STORAGE_PREFIX}${userId}`);
    if (!savedActionsJson) return [...DEFAULT_ENABLED_ACTIONS];

    const parsedActions: unknown = JSON.parse(savedActionsJson);
    if (!Array.isArray(parsedActions)) return [...DEFAULT_ENABLED_ACTIONS];

    const savedActionIds = Array.from(new Set(parsedActions.filter(
      (actionId): actionId is string => typeof actionId === 'string' && QUICK_ACTION_IDS.has(actionId)
    )));
    const version = localStorage.getItem(`${QUICK_ACTION_STORAGE_PREFIX}${userId}${QUICK_ACTION_STORAGE_VERSION_SUFFIX}`);
    if (!version) {
      return [...savedActionIds, ...NEWLY_INTRODUCED_ACTIONS.filter(actionId => !savedActionIds.includes(actionId))];
    }
    return savedActionIds;
  } catch {
    return [...DEFAULT_ENABLED_ACTIONS];
  }
};

const QuickActions: React.FC<QuickActionsProps> = ({
  onAddCustomer,
  onAddItem,
  onSearchCustomer,
  onAddDebt,
  onAddPayment,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  
  // State for customization
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [enabledActions, setEnabledActions] = useState<string[]>(() => readEnabledActions(user?.id));
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [previewActions, setPreviewActions] = useState<string[] | null>(null);
  const [pointerDeltaY, setPointerDeltaY] = useState(0);
  const dragSessionRef = useRef<{
    id: string;
    order: string[];
    input: 'native' | 'pointer';
    startY: number;
  } | null>(null);
  const touchPointerIdRef = useRef<number | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const rowMetricsRef = useRef<Map<string, DragRowMetric>>(new Map());
  const previousPositionsRef = useRef<Map<string, DOMRect>>(new Map());
  const rowAnimationsRef = useRef<Map<string, Animation>>(new Map());

  useEffect(() => {
    if (!user?.id) return;

    try {
      localStorage.setItem(
        `${QUICK_ACTION_STORAGE_PREFIX}${user.id}`,
        JSON.stringify(enabledActions)
      );
      localStorage.setItem(
        `${QUICK_ACTION_STORAGE_PREFIX}${user.id}${QUICK_ACTION_STORAGE_VERSION_SUFFIX}`,
        QUICK_ACTION_STORAGE_VERSION,
      );
    } catch {
      // Keep the current session usable when browser storage is unavailable.
    }
  }, [enabledActions, user?.id]);

  useLayoutEffect(() => {
    const previousPositions = previousPositionsRef.current;
    if (previousPositions.size === 0) return;

    previousPositions.forEach((previousRect, id) => {
      const row = rowRefs.current[id];
      if (!row) return;

      rowAnimationsRef.current.get(id)?.cancel();
      rowAnimationsRef.current.delete(id);
      const distance = previousRect.top - row.getBoundingClientRect().top;
      if (prefersReducedMotion || Math.abs(distance) < 1 || !row.animate) return;

      const animation = row.animate(
        [{ transform: `translateY(${distance}px)` }, { transform: 'translateY(0)' }],
        { duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
      );
      rowAnimationsRef.current.set(id, animation);
      animation.onfinish = () => {
        if (rowAnimationsRef.current.get(id) === animation) {
          rowAnimationsRef.current.delete(id);
        }
      };
    });
    previousPositions.clear();
  }, [enabledActions, previewActions, prefersReducedMotion]);

  // Define all available actions
  const allActions: QuickAction[] = [
    {
      id: 'new-sale',
      title: t('quickActions.newSale'),
      description: t('quickActions.newSaleDesc'),
      icon: <NewSaleIcon />,
      action: () => navigate('/sales'),
      accent: 'sales',
      requiredRoles: [Role.COMPANY_ADMIN, Role.EMPLOYEE],
      category: 'other',
    },
    {
      id: 'add-customer',
      title: t('quickActions.addCustomer'),
      description: t('quickActions.addCustomerDesc'),
      icon: <AddCustomerIcon />,
      action: () => {
        if (onAddCustomer) {
          onAddCustomer();
        } else {
          navigate('/customers');
        }
      },
      accent: 'customers',
      requiredRoles: [Role.COMPANY_ADMIN],
      category: 'customer',
    },
    {
      id: 'add-item',
      title: t('quickActions.addItem'),
      description: t('quickActions.addItemDesc'),
      icon: <AddItemIcon />,
      action: () => {
        if (onAddItem) {
          onAddItem();
        } else {
          navigate('/items');
        }
      },
      accent: 'items',
      requiredRoles: [Role.COMPANY_ADMIN],
      category: 'item',
    },
    {
      id: 'add-debt',
      title: t('quickActions.addDebt'),
      description: t('quickActions.addDebtDesc'),
      icon: <AddDebtIcon />,
      action: () => {
        if (onAddDebt) {
          onAddDebt();
        } else {
          navigate('/debts');
        }
      },
      accent: 'debts',
      requiredRoles: [Role.COMPANY_ADMIN, Role.EMPLOYEE],
      category: 'debt',
    },
    {
      id: 'add-payment',
      title: t('quickActions.addPayment'),
      description: t('quickActions.addPaymentDesc'),
      icon: <AddPaymentIcon />,
      action: () => {
        if (onAddPayment) {
          onAddPayment();
        } else {
          navigate('/debts');
        }
      },
      accent: 'debts',
      requiredRoles: [Role.COMPANY_ADMIN, Role.EMPLOYEE],
      category: 'debt',
    },
    {
      id: 'search-customer',
      title: t('quickActions.searchCustomer'),
      description: t('quickActions.searchCustomerDesc'),
      icon: <SearchCustomerIcon />,
      action: () => onSearchCustomer ? onSearchCustomer() : navigate('/customers?focus=search'),
      accent: 'customers',
      requiredRoles: [Role.SUPER_ADMIN, Role.COMPANY_ADMIN, Role.EMPLOYEE],
      category: 'search',
    },
    {
      id: 'browse-items',
      title: t('quickActions.browseItems'),
      description: t('quickActions.browseItemsDesc'),
      icon: <BrowseItemsIcon />,
      action: () => navigate('/items?focus=search'),
      accent: 'items',
      requiredRoles: [Role.SUPER_ADMIN, Role.COMPANY_ADMIN, Role.EMPLOYEE],
      category: 'item',
    },
    {
      id: 'scan-barcode',
      title: t('barcode.scan'),
      description: t('quickActions.scanBarcodeDesc'),
      icon: <ScanBarcodeIcon />,
      action: () => setScanDialogOpen(true),
      accent: 'items',
      requiredRoles: [Role.SUPER_ADMIN, Role.COMPANY_ADMIN, Role.EMPLOYEE],
      category: 'item',
    },
    // Future actions (disabled by default)
    {
      id: 'add-user',
      title: t('quickActions.addUser'),
      description: t('quickActions.addUserDesc'),
      icon: <PersonAdd />,
      action: () => navigate('/users'),
      accent: 'users',
      requiredRoles: [Role.SUPER_ADMIN, Role.COMPANY_ADMIN],
      category: 'user',
    },
  ];

  const visibleActionsFor = (ids: string[]): QuickAction[] => ids
    .map(actionId => allActions.find(action => action.id === actionId))
    .filter((action): action is QuickAction => {
      if (!action) return false;
      return !action.requiredRoles || !user?.role || action.requiredRoles.includes(user.role);
    });

  // Keep the DOM stable during a drag, then use transforms to preview the new order.
  const availableActions = visibleActionsFor(enabledActions);
  const dialogActions = availableActions;
  const previewDialogActions = visibleActionsFor(previewActions ?? enabledActions);
  const rowOffsets = new Map<string, number>();
  if (previewActions && dialogActions.length > 0) {
    let nextTop = rowMetricsRef.current.get(dialogActions[0].id)?.top ?? 0;
    previewDialogActions.forEach(action => {
      const metric = rowMetricsRef.current.get(action.id);
      if (!metric) return;
      rowOffsets.set(action.id, nextTop - metric.top);
      nextTop += metric.height + metric.marginBottom;
    });
  }

  const captureRowPositions = () => {
    previousPositionsRef.current = new Map(
      dialogActions.flatMap(action => {
        const row = rowRefs.current[action.id];
        return row ? [[action.id, row.getBoundingClientRect()] as const] : [];
      })
    );
  };

  const cancelDrag = () => {
    dragSessionRef.current = null;
    touchPointerIdRef.current = null;
    rowMetricsRef.current.clear();
    setPreviewActions(null);
    setDraggedItem(null);
    setPointerDeltaY(0);
  };

  const commitDrag = () => {
    const order = dragSessionRef.current?.order;
    if (order) captureRowPositions();
    dragSessionRef.current = null;
    touchPointerIdRef.current = null;
    rowMetricsRef.current.clear();
    if (order) setEnabledActions(order);
    setPreviewActions(null);
    setDraggedItem(null);
    setPointerDeltaY(0);
  };

  const startDrag = (actionId: string, input: 'native' | 'pointer', startY: number) => {
    rowMetricsRef.current = new Map(dialogActions.flatMap(action => {
      const row = rowRefs.current[action.id];
      if (!row) return [];
      return [[action.id, {
        top: row.offsetTop,
        height: row.offsetHeight,
        marginBottom: parseFloat(window.getComputedStyle(row).marginBottom) || 0,
      }] as const];
    }));
    dragSessionRef.current = { id: actionId, order: enabledActions, input, startY };
    setPreviewActions(enabledActions);
    setDraggedItem(actionId);
    setPointerDeltaY(0);
  };

  const updateDragPreview = (clientY: number) => {
    const session = dragSessionRef.current;
    if (!session) return;

    const visibleIds = visibleActionsFor(session.order).map(action => action.id);
    const otherIds = visibleIds.filter(id => id !== session.id);
    const beforeIndex = otherIds.findIndex(id => {
      const row = rowRefs.current[id];
      if (!row) return false;
      const rect = row.getBoundingClientRect();
      return clientY < rect.top + rect.height / 2;
    });
    const nextVisibleIds = [...otherIds];
    nextVisibleIds.splice(beforeIndex === -1 ? otherIds.length : beforeIndex, 0, session.id);

    if (nextVisibleIds.every((id, index) => id === visibleIds[index])) return;

    const nextOrder = arrangeVisibleActions(session.order, visibleIds, nextVisibleIds);
    session.order = nextOrder;
    setPreviewActions(nextOrder);
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    cancelDrag();
    setSettingsOpen(false);
  };

  const handleActionToggle = (actionId: string) => {
    setEnabledActions(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  const handleDragStart = (e: React.DragEvent, actionId: string) => {
    if (touchPointerIdRef.current !== null) {
      e.preventDefault();
      return;
    }
    startDrag(actionId, 'native', e.clientY);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', actionId);
    const row = rowRefs.current[actionId];
    if (row) {
      const rect = row.getBoundingClientRect();
      e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!dragSessionRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    updateDragPreview(e.clientY);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    commitDrag();
  };

  const handleDragEnd = () => {
    if (dragSessionRef.current) cancelDrag();
  };

  const handlePointerDown = (e: React.PointerEvent, actionId: string) => {
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    touchPointerIdRef.current = e.pointerId;
    startDrag(actionId, 'pointer', e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (touchPointerIdRef.current !== e.pointerId) return;
    const session = dragSessionRef.current;
    if (session) setPointerDeltaY(e.clientY - session.startY);
    updateDragPreview(e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (touchPointerIdRef.current !== e.pointerId) return;
    commitDrag();
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    if (touchPointerIdRef.current !== e.pointerId) return;
    cancelDrag();
  };

  const handleReorderKeyDown = (e: React.KeyboardEvent, actionId: string) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();

    const visibleIds = availableActions.map(action => action.id);
    const currentIndex = visibleIds.indexOf(actionId);
    const nextIndex = currentIndex + (e.key === 'ArrowUp' ? -1 : 1);
    if (currentIndex === -1 || nextIndex < 0 || nextIndex >= visibleIds.length) return;

    const nextVisibleIds = [...visibleIds];
    nextVisibleIds.splice(currentIndex, 1);
    nextVisibleIds.splice(nextIndex, 0, actionId);
    captureRowPositions();
    setEnabledActions(arrangeVisibleActions(enabledActions, visibleIds, nextVisibleIds));
  };

  return (
    <>
      <Paper data-testid="dashboard-quick-actions" sx={{ p: { xs: 1.5, md: 3 }, mb: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('dashboard.quickActions')}
          </Typography>
          <Tooltip title={t('quickActions.customizeActions')}>
            <IconButton
              onClick={handleSettingsOpen}
              aria-label={t('quickActions.customizeActions')}
              sx={{
                '& .quick-actions-settings-icon': { transition: 'transform 400ms ease-in-out' },
                '&:hover .quick-actions-settings-icon': { transform: 'rotate(360deg)' },
                '@media (prefers-reduced-motion: reduce)': {
                  '& .quick-actions-settings-icon': { transition: 'none' },
                },
              }}
            >
              <SettingsIcon className="quick-actions-settings-icon" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, 1fr)' },
            gap: { xs: 0.75, md: 1.5 }
          }}
        >
          {availableActions.map((action) => (
            <Card key={action.id} 
                sx={theme => ({
                  height: '100%',
                  border: 0,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0, 0, 0, .34)'
                    : '0 2px 8px rgba(32, 55, 44, .14)',
                  background: isDesktop ? `linear-gradient(120deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})` : theme.palette.action.hover,
                  ...createInteractiveCardSx(theme),
                })}
              >
                <CardActionArea onClick={action.action} sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, justifyContent: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' }, gap: { xs: 0.75, md: 1.5 }, p: { xs: 1, md: 2 }, minHeight: { xs: 104, md: 0 }, '&:last-child': { pb: { xs: 1, md: 2 } } }}>
                    <PageIconTile icon={action.icon} accent={action.accent} size={isDesktop ? 42 : 32} />
                    <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
                      <Typography fontWeight={700} sx={{ fontSize: { xs: 15, md: 16 }, lineHeight: 1.2 }}>{action.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>{action.description}</Typography>
                    </Box>
                    <ArrowForward sx={{ color: 'text.secondary', fontSize: { xs: 18, md: 19 }, mt: { md: .5 }, display: { xs: 'none', md: 'block' } }} />
                  </CardContent>
                </CardActionArea>
              </Card>
          ))}
        </Box>

        {availableActions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('quickActions.noActionsAvailable')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Customization Dialog */}
      <AppDialog presentation="workflow" open={settingsOpen} onClose={handleSettingsClose} maxWidth="sm" fullWidth>
        <AppDialogHeader icon={<SettingsIcon />}>
          {t('quickActions.customizeActions')}
        </AppDialogHeader>
        <DialogContent>
          {/* Enabled Actions - Sortable */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('quickActions.enabledActions')}
          </Typography>
          <FormGroup sx={{ mb: 3 }} onDragOver={handleDragOver} onDrop={handleDrop}>
            {dialogActions.map((action) => {
              const isDragged = draggedItem === action.id;
              const pointerDragged = isDragged && dragSessionRef.current?.input === 'pointer';
              const offset = pointerDragged ? pointerDeltaY : rowOffsets.get(action.id) ?? 0;
              return (
                <Box
                  key={action.id}
                  component="div"
                  ref={node => { rowRefs.current[action.id] = node as HTMLDivElement | null; }}
                  style={{
                    transform: `translate3d(0, ${offset}px, 0)`,
                    transition: prefersReducedMotion
                      ? 'none'
                      : pointerDragged
                      ? 'background-color 140ms ease, opacity 140ms ease'
                      : 'transform 180ms cubic-bezier(0.2, 0, 0, 1), background-color 140ms ease, opacity 140ms ease',
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: pointerDragged ? 2 : 1,
                    p: 1,
                    border: '1px solid',
                    borderColor: isDragged ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: isDragged ? 'action.selected' : 'transparent',
                    opacity: isDragged && !pointerDragged ? 0.2 : 1,
                    boxShadow: pointerDragged ? 4 : 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <IconButton
                    size="small"
                    draggable
                    onDragStart={e => handleDragStart(e, action.id)}
                    onDragEnd={handleDragEnd}
                    onPointerDown={e => handlePointerDown(e, action.id)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerCancel}
                    onKeyDown={e => handleReorderKeyDown(e, action.id)}
                    aria-label={`${action.title}: ${t('quickActions.dragToReorder')}`}
                    sx={{ mr: 1, cursor: 'grab', touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
                  >
                    <DragIcon sx={{ color: 'text.secondary' }} />
                  </IconButton>
                  <Checkbox
                    checked={true}
                    onChange={() => handleActionToggle(action.id)}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ mr: 1 }}><PageIconTile icon={action.icon} accent={action.accent} size={30} iconSize={18} /></Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {action.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </FormGroup>

          {/* Disabled Actions */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('quickActions.availableActions')}
          </Typography>
          <FormGroup>
            {allActions
              .filter(action => 
                !enabledActions.includes(action.id) && 
                (!action.requiredRoles || action.requiredRoles.includes(user?.role!))
              )
              .map((action) => (
                <FormControlLabel
                  key={action.id}
                  control={
                    <Checkbox
                      checked={false}
                      onChange={() => handleActionToggle(action.id)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PageIconTile icon={action.icon} accent={action.accent} size={30} iconSize={18} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {action.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.description}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSettingsClose}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </AppDialog>
      <BarcodeScannerDialog
        open={scanDialogOpen}
        onClose={() => setScanDialogOpen(false)}
        onDetected={(barcode) => {
          setScanDialogOpen(false);
          navigate(`/items?focus=search&barcode=${encodeURIComponent(barcode)}`);
        }}
        title={t('quickActions.scanBarcode')}
      />
    </>
  );
};

export default QuickActions;
